import { useEffect, useRef, useState } from 'react';

import { AlertTriangleIcon, XIcon } from 'lucide-react';
import { FormProvider, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { LanguageFlag } from '@/components/atoms/FlagEmoji/LanguageFlag';
import {
  SidebarWrapper,
  SidebarWrapperContent,
  SidebarWrapperFooter,
  SidebarWrapperForm,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { useResponsiveSidebarClose } from '@/components/organisms/Sidebar/ResponsiveSidebarPanel';
import { Sidebar } from '@/components/organisms/Sidebar/Sidebar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useSidebar } from '@/hooks/useSidebar';
import {
  selectIsSpeechStrategyDirty,
  SPEECH_STRATEGY_FORM_ID,
  useSpeechStrategyForm,
} from '@/modules/aiAgents/hooks/useSpeechStrategyForm';
import { SpeechKind } from '@/shared/types/speechStrategy';
import { formatLanguageLabel } from '@/util/languageOptions';

import { SPEECH_KINDS, SpeechStrategyForm } from './helpers';
import { SpeechStepEditor } from './SpeechStepEditor';

const KIND_LABEL_KEYS: Record<SpeechKind, string> = {
  stt: 'ai_agents.speech_strategy.speech_to_text',
  tts: 'ai_agents.speech_strategy.text_to_speech',
};

/** Wide enough for the provider cards: about 500 px at 1440 and 450 px at 1280. */
const SPEECH_PANEL_SIZE = { id: 'speech-language', defaultSize: 35, minSize: 30, maxSize: 50 };

interface KindEditorProps {
  kind: SpeechKind;
  language: string;
  isPrimary: boolean;
  disabled: boolean;
}

const KindEditor = ({ kind, language, isPrimary, disabled }: KindEditorProps) => {
  const { t } = useTranslation();
  const entries = useWatch<SpeechStrategyForm, SpeechKind>({ name: kind });
  const index = (entries ?? []).findIndex((entry) => entry.language === language);

  if (index < 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4" data-testid={`speech-${kind}-${language}`}>
      <h3 className="text-base leading-6 font-semibold text-foreground">
        {t(KIND_LABEL_KEYS[kind])}
      </h3>
      <SpeechStepEditor
        kind={kind}
        name={`${kind}.${index}.step`}
        language={language}
        isPrimary={isPrimary}
        disabled={disabled}
      />
    </div>
  );
};

interface SpeechStrategySectionProps {
  brainId: string;
  /** The language the panel edits, and its key in the strategy. */
  language: string;
  primaryLanguage?: string;
}

/** The speech-to-text and text-to-speech settings of one language, in the side panel. */
export const SpeechStrategySection = ({
  brainId,
  language,
  primaryLanguage,
}: SpeechStrategySectionProps) => {
  const { t } = useTranslation();
  const close = useResponsiveSidebarClose();
  const isPrimary = primaryLanguage === language;
  const titleRef = useRef<HTMLHeadingElement>(null);

  const [isUnsavedOpen, setIsUnsavedOpen] = useState(false);
  const { form, onSubmit, loadStatus, reload, canRead, canWrite, isDirty, isMutating } =
    useSpeechStrategyForm({ brainId, language, isPrimary });

  // The panel opens beside the page, so move keyboard focus into it
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  const handleClose = () => {
    if (isDirty) {
      setIsUnsavedOpen(true);
      return;
    }
    close();
  };

  // An open list or dialog takes Escape for itself
  useEscapeKey(() => {
    if (!document.querySelector('[data-radix-popper-content-wrapper], [role="dialog"]')) {
      handleClose();
    }
  });

  if (!canRead) {
    return null;
  }

  const renderBody = () => {
    if (loadStatus === 'error') {
      return (
        <Alert variant="destructive">
          <AlertTriangleIcon />
          <AlertDescription className="flex items-center justify-between gap-3">
            {t('ai_agents.speech_strategy.load_failed')}
            <Button type="button" variant="outline-neutral" size="sm" onClick={() => reload()}>
              {t('common.try_again')}
            </Button>
          </AlertDescription>
        </Alert>
      );
    }
    if (loadStatus !== 'success') {
      return <Skeleton className="h-32 w-full" />;
    }
    return SPEECH_KINDS.map((kind, index) => (
      <div key={kind} className="flex flex-col gap-6">
        {index > 0 && <Separator />}
        <KindEditor kind={kind} language={language} isPrimary={isPrimary} disabled={!canWrite} />
      </div>
    ));
  };

  const handleSave = async (values: SpeechStrategyForm) => {
    await onSubmit(values);
    close();
  };

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2
          ref={titleRef}
          tabIndex={-1}
          className="flex items-center gap-2 text-base leading-5 font-medium outline-none"
        >
          <LanguageFlag code={language} />
          {formatLanguageLabel(language)}
        </h2>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          aria-label={t('common.close')}
          onClick={handleClose}
        >
          <XIcon size={16} />
        </Button>
      </SidebarWrapperTitle>
      <FormProvider {...form}>
        <SidebarWrapperForm id={SPEECH_STRATEGY_FORM_ID} onSubmit={form.handleSubmit(handleSave)}>
          <SidebarWrapperContent className="flex flex-col gap-6 space-y-0">
            {renderBody()}
          </SidebarWrapperContent>
          <SidebarWrapperFooter>
            <Button type="submit" isLoading={isMutating} disabled={!isDirty || !canWrite}>
              {t('common.save')}
            </Button>
            <Button
              type="button"
              variant="ghost-neutral"
              onClick={handleClose}
              disabled={isMutating}
            >
              {t('common.cancel')}
            </Button>
          </SidebarWrapperFooter>
        </SidebarWrapperForm>
      </FormProvider>
      {isUnsavedOpen && (
        <UnsavedChangesModal
          onDiscard={close}
          onGoBack={() => setIsUnsavedOpen(false)}
          onOpenChange={setIsUnsavedOpen}
        />
      )}
    </SidebarWrapper>
  );
};

interface SpeechLanguageSidebarProps {
  brainId: string;
  primaryLanguage?: string;
}

/** The side panel of the language the sidebar meta names; a dirty form keeps it from being swiped away. */
export const SpeechLanguageSidebar = ({ brainId, primaryLanguage }: SpeechLanguageSidebarProps) => {
  const { t } = useTranslation();
  const { meta } = useSidebar();
  const isDirty = useSelector(selectIsSpeechStrategyDirty);
  const language = meta?.language;

  if (!language) {
    return null;
  }

  return (
    <Sidebar
      when="speech_language"
      title={t('ai_agents.languages.speech_configurations')}
      size={SPEECH_PANEL_SIZE}
      dismissible={!isDirty}
    >
      <SpeechStrategySection
        key={language}
        brainId={brainId}
        language={language}
        primaryLanguage={primaryLanguage}
      />
    </Sidebar>
  );
};
